import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate } from 'react-router-dom'

import Button from '@/components/common/Button/Button'

import MotionText from '@/components/common/Motion/MotionText'
import ProgressBar from '@components/common/ProgressBar/ProgressBar'
import PhoneInput from '@/components/common/Input/PhoneInput'
import HeaderBack from '@/components/dashboard/Header/HeaderBack'

import { useKycStore } from '@store/useKycStore'
import { useUserStore } from '@/store/useUserStore'
import { usePageSetup } from '@/hooks/usePageSetup'

import mfaAPI from '@/services/api/mfa'

export const KycMfaPage = () => {
  const { t } = useTranslation()
  usePageSetup(t('kyc.pageTitle.mfa'), { scroll: true })

  const user = useUserStore((state) => state.user)

  if (user?.mfa) {
    return <Navigate to="/" replace />
  }

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | undefined>()

  const navigate = useNavigate()

  const progress = useKycStore((state) => state.progress)
  const setProgress = useKycStore((state) => state.setProgress)

  useEffect(() => setProgress(7), [])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const formData = new FormData(e.currentTarget)
    const phone = formData.get('phone') as string

    try {
      setIsLoading(true)

      await mfaAPI.setup({ mfa: { phone } })

      navigate("/kyc/mfacode", { state: { phone } })
    } catch (error: any) {
      setError(t('kyc.mfa.invalidPhone'))
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="h-full bg-white p-5 pb-8 flex flex-col">
      <HeaderBack className='items-center gap-4'>
        <ProgressBar
          currentStep={progress}
          totalSteps={8}
        />
      </HeaderBack>

      <div className="flex-1 flex flex-col mt-4">
        <div className="space-y-4">
          <MotionText
            as="h2"
            className="text-zinc-900 text-3xl font-bold leading-tight"
            delay={0.2}
          >
            {t('kyc.mfa.title')}
          </MotionText>
          <MotionText
            as="p"
            className="text-zinc-700 text-base"
            delay={0.4}
          >
            {t('kyc.mfa.description')}
          </MotionText>
        </div>
        <motion.div className='my-6'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.3,
            ease: "easeInOut"
          }}
        >
          <PhoneInput
            required
            name='phone'
            error={error}
          />
        </motion.div>
      </div>

      <div className="flex flex-row gap-4">
        <Button type='submit' isLoading={isLoading}>
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.2,
              ease: "easeInOut"
            }}
          >
            {t('common.continue')}
          </motion.span>
        </Button>
      </div>
    </form>
  )
}

export default KycMfaPage